import React, { FunctionComponent } from 'react';

import axios, { AxiosResponse } from 'axios';
import { each, set, isObject } from 'lodash';
import { Container, Col, Row } from 'react-bootstrap';

import { ModuleId, ModuleSourceRule } from 'admin/modules/Modules';
import { SourceId } from 'admin/sources/Sources';
import { SearchRequest } from 'core/util/Search';
import { LimitedTypes } from 'directory/common/util/CommonTypes';

export interface Tenant {
    id: number;
    agencyID: number;
    name: string;
    tenantType: TenantType;
    accountType: AccountType;
    moduleIDs: string[];
    moduleSources?: Record<ModuleId, SourceId[]>;
    moduleSourceRules?: ModuleSourceRule[];
    sourceGroups?: string[] | SourceGroup;
    support?: SupportType;
    smallLogoPath?: string;
    largeLogoPath?: string;
    industry?: Record<string, any> | string;
    attributeDefinitions?: AttributeDefinition[];
    integration?: {
        enabled: boolean;
        type: string;
    };
    createdDate?: number;
    autofindEnabled?: boolean;
}

export interface AttributeDefinition {
    value: string;
    custom: boolean;
    entityAttributeName: string;
    label: string;
    modules?: string[];
    multiSelect: boolean;
    name: LimitedTypes;
    order: number;
    restricted: boolean;
    sortable: true;
    visible: true;
}

export enum TenantType {
    Enterprise = 1,
    SmallBusiness = 2,
    Vendor = 3,
}

export type SupportType = {
    viaEmail?: boolean;
    viaPhone?: boolean;
    supportEmail?: string;
    list: { name?: string; link?: string }[];
};

export enum AccountType {
    Current = 1,
    Demo = 2,
    Delinquent = 3,
    Benchmark = 4,
    Competitor = 5,
    FTC = 6,
    QA = 7,
    FreeTrial = 8,
    Sandbox = 9,
    Competitor2 = 10,
}

export interface SourceGroup {
    id: string;
    name: string;
    sources: Source[];
}

interface Source {
    id: SourceId;
    name: string;
}

export enum TenantId {
    Bozzuto = 1274,
    FordDirect = 289,
    QATestInbox = 10,
    QATestMessage = 29,
    QATestSudha = 1990,
    QAAutomation = 123016,
    QAAutomation2 = 2003002,
}

export enum FieldName {
    Id = '__tenant_id__',
    // AccountType = '__tenant_accountType__',
    Active = '__tenant_active__',
    AgencyId = '__tenant_agencyID__',
    Name = '__tenant_name__',
    Search = '__tenant_search__',
    TenantId = '__tenant_tenant_id__',
    ModuleId = '__tenant_moduleID__',
    // TenantType = '__tenant_tenantType__',
}

export interface GetTenantsArgs {
    basic?: boolean;
    includeSourceGroups?: boolean;
    runAsAgencyUser?: boolean;
    runAsSuperAgencyUser?: boolean;
}

interface TenantSetting {
    key: string;
    value: any;
}

export const getTenants = async (
    agencyId: number,
    search?: SearchRequest,
    args?: GetTenantsArgs,
): Promise<Tenant[]> => {
    const params: any = { agencyId, search, args };

    if (args && args.runAsAgencyUser) {
        params.runAsAgencyUser = args.runAsAgencyUser;
    }

    const response: AxiosResponse = await axios.get('/api/tenants', {
        params,
    });

    return response.data;
};

export const getTenant = async (agencyId: number, tenantId: number, args?: GetTenantsArgs): Promise<Tenant> => {
    const params: any = { agencyId, tenantId, args };

    if (args && args.runAsAgencyUser) {
        params.runAsAgencyUser = args.runAsAgencyUser;
    }

    const response: AxiosResponse = await axios.get(`/api/tenants/${tenantId}`, {
        params,
    });

    return response.data;
};

export const saveTenant = async (agencyId: number, tenantId: number, tenant: Tenant): Promise<Tenant> => {
    const params: any = { agencyId, tenantId };

    const response: AxiosResponse = await axios.post('/api/tenants', { params, tenant });

    return response.data;
};

export const saveTenantSettings = async (
    agencyId: number,
    tenantId: number,
    settings: TenantSetting[],
): Promise<Tenant> => {
    const tenant = await getTenant(agencyId, tenantId, { basic: false });

    each(settings, ({ key, value }) => set(tenant, key, value));

    if (isObject(tenant?.industry)) {
        tenant.industry = tenant.industry.id;
    }

    const savedTenant = await saveTenant(agencyId, tenantId, tenant);

    return savedTenant;
};

export const getIndustries = async (agencyId: number, tenantId: number): Promise<Record<string, any>> => {
    const params: any = { agencyId, tenantId, args: { basic: true } };

    const { data } = await axios.get(`/api/tenants/${tenantId}/industries`, {
        params,
    });

    return data;
};

export const getGroups = async (tenantId: number): Promise<Record<string, any>> => {
    const params: any = { tenantId, args: { complete: true, all: true } };

    const { data } = await axios.get(`/api/tenants/${tenantId}/groups`, {
        params,
    });

    return data;
};

export const hasModule = (tenant: Tenant | undefined, moduleId: string): boolean => {
    if (tenant === undefined) {
        return false;
    }

    return tenant.moduleIDs?.includes(moduleId);
};

export const existsTenantByName = async (agencyId: number, tenantId: number, tenantName: string): Promise<any> => {
    // TODO: change response type
    const params: any = { agencyId, tenantId, tenantName };
    const { data }: AxiosResponse = await axios.put(`/api/tenants/exists`, params);

    return data;
};

export const Tenants: FunctionComponent = () => {
    return (
        <Container fluid>
            <Row>
                <Col>Tenants</Col>
            </Row>
        </Container>
    );
};
